import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translateFanTitle } from '@utils/fanTranslations';
import { CarouselLeftArrowIcon } from '../Icons/CarouselLeftArrowIcon';
import { CarouselRightArrowIcon } from '../Icons/CarouselRightArrowIcon';

export interface CarouselItem {
  video: string;
  image: string;
  albumName?: string;
  artistName?: string;
  artistAliasTag?: string;
  title?: string;
  slug?: string;
}

interface FanCarouselProps {
  items: CarouselItem[];
  className?: string;
  onItemClick?: (index: number) => void;
  onCenteredItemChange?: (index: number) => void;
  selectedContributorSlug: string | null;
}

const RADIUS = 240; // Radius of the semicircular arc
const MAX_ANGLE = 90; // Total arc span in degrees
const VISIBLE_ITEMS = 3; // number of items visible on each side of center
const DRAG_SENSITIVITY = 80; // pixels per item
const MIN_VELOCITY_FOR_MOMENTUM = 0.05;

// video item component for carousel
const VideoItem: React.FC<{
  item: CarouselItem;
  index: number;
  animatedIndex: number;
  totalItems: number;
  randomStartTime: number;
  onPress: () => void;
}> = ({ item, index, animatedIndex, totalItems, randomStartTime, onPress }) => {
  const [zIndex, setZIndex] = useState(50);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const checkVisibility = (animatedValue: number) => {
      const rawRelativePos = animatedValue - index;
      const cycle = Math.round(rawRelativePos / totalItems);
      const itemCenterPosition = index + cycle * totalItems;

      const distanceFromCenter = Math.abs(animatedValue - itemCenterPosition);
      const shouldBeVisible = distanceFromCenter <= VISIBLE_ITEMS + 1;

      setIsVisible(shouldBeVisible);
    };

    checkVisibility(animatedIndex);
  }, [animatedIndex, index, totalItems]);

  useEffect(() => {
    const updateZIndex = (animatedValue: number) => {
      const rawRelativePos = animatedValue - index;

      // find nearest cycle
      const cycle = Math.round(rawRelativePos / totalItems);
      const relativePos = rawRelativePos - cycle * totalItems;

      const absValue = Math.abs(relativePos);
      const newZIndex =
        absValue === 0
          ? 100
          : absValue <= 1
            ? 80 - absValue * 30
            : absValue <= 2
              ? 50 - (absValue - 1) * 20
              : Math.max(1, 10 - absValue);
      setZIndex(Math.max(1, Math.round(newZIndex)));
    };

    updateZIndex(animatedIndex);
  }, [animatedIndex, index, totalItems]);

  if (!isVisible) return null;

  const interpolate = (
    value: number,
    inputRange: number[],
    outputRange: number[],
    extrapolate: 'clamp' | 'extend' = 'clamp',
  ): number => {
    let clampedValue = value;
    if (extrapolate === 'clamp') {
      clampedValue = Math.max(
        inputRange[0],
        Math.min(inputRange[inputRange.length - 1], value),
      );
    }

    for (let i = 0; i < inputRange.length - 1; i++) {
      if (clampedValue >= inputRange[i] && clampedValue <= inputRange[i + 1]) {
        const inputMin = inputRange[i];
        const inputMax = inputRange[i + 1];
        const outputMin = outputRange[i];
        const outputMax = outputRange[i + 1];

        if (inputMin === inputMax) return outputMin;

        const ratio = (clampedValue - inputMin) / (inputMax - inputMin);
        return outputMin + ratio * (outputMax - outputMin);
      }
    }

    if (clampedValue <= inputRange[0]) return outputRange[0];
    return outputRange[outputRange.length - 1];
  };

  const rawRelativePos = animatedIndex - index;
  const cycle = Math.round(rawRelativePos / totalItems);
  const relativePosition = rawRelativePos - cycle * totalItems;

  const numPoints = VISIBLE_ITEMS * 2 + 1;
  const angleInputs: number[] = [];
  const xOutputs: number[] = [];
  const yOutputs: number[] = [];

  for (let i = 0; i <= numPoints; i++) {
    const pos = -VISIBLE_ITEMS + (i * (VISIBLE_ITEMS * 2)) / numPoints;
    const angleDeg = (pos / VISIBLE_ITEMS) * (MAX_ANGLE / 2);
    const angleRad = (angleDeg * Math.PI) / 180;

    angleInputs.push(pos);
    xOutputs.push(Math.sin(angleRad) * RADIUS);
    yOutputs.push(RADIUS - Math.cos(angleRad) * RADIUS);
  }

  const x = interpolate(relativePosition, angleInputs, xOutputs, 'clamp');
  const y = interpolate(relativePosition, angleInputs, yOutputs, 'clamp');

  const scale = interpolate(
    relativePosition,
    [-VISIBLE_ITEMS, -2, -1, 0, 1, 2, VISIBLE_ITEMS],
    [0.6, 0.7, 0.85, 1.1, 0.85, 0.7, 0.6],
    'clamp',
  );

  const opacity = interpolate(
    relativePosition,
    [-VISIBLE_ITEMS - 1, -VISIBLE_ITEMS, 0, VISIBLE_ITEMS, VISIBLE_ITEMS + 1],
    [0, 0.7, 1.0, 0.7, 0],
    'clamp',
  );

  const distanceFromCenter = Math.abs(relativePosition);

  const ITEM_SIZE = 100;

  return (
    <video
      className="object-cover cursor-pointer absolute rounded-full shadow-xl border-2 border-white/20"
      src={item.video}
      poster={item.image}
      autoPlay
      loop
      muted
      playsInline
      style={{
        width: ITEM_SIZE,
        height: ITEM_SIZE,
        left: '50%',
        top: '50%',
        marginLeft: `-${ITEM_SIZE / 2}px`,
        marginTop: `-${ITEM_SIZE / 2}px`,
        transform: `translate3d(${x}px, ${y}px, 0px) scale(${scale})`,
        zIndex: zIndex,
        opacity: opacity,
        filter:
          distanceFromCenter === 0
            ? 'brightness(1.2) saturate(1.2)'
            : 'brightness(0.8)',
        transition: 'none',
      }}
      onClick={(e) => {
        e.stopPropagation();
        onPress();
      }}
      onLoadedMetadata={(e) => {
        const videoElement = e.currentTarget;
        if (videoElement.duration) {
          videoElement.currentTime = randomStartTime;
        }
      }}
    />
  );
};

export const FanCarousel: React.FC<FanCarouselProps> = ({
  items,
  className = '',
  onItemClick,
  onCenteredItemChange,
  selectedContributorSlug,
}) => {
  const { t } = useTranslation();
  const [scrollIndex, setScrollIndex] = useState(0);
  const animatedIndexRef = useRef(0);
  const dragOffsetRef = useRef(0);
  const combinedAnimatedIndexRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isMomentumScrollingRef = useRef(false);
  const startAnimatedValueRef = useRef(0);
  const currentCombinedValueRef = useRef(0);
  const animationFrameRef = useRef<number | null>(null);
  const momentumAnimationRef = useRef<{
    velocity: number;
    deceleration: number;
    startTime: number;
    startValue: number;
  } | null>(null);
  const [shouldRunMomentum, setShouldRunMomentum] = useState(false);

  const startXRef = useRef(0);
  const lastXRef = useRef(0);
  const lastTimeRef = useRef(0);
  const velocityRef = useRef(0);

  const tickSoundRef = useRef<HTMLAudioElement | null>(null);
  const previousCenteredCardRef = useRef<number | null>(null);
  const lastIntegerCrossedRef = useRef<number | null>(null);
  const lastAnimatedValueRef = useRef<number | null>(null);

  const [animatedIndex, setAnimatedIndex] = useState(0);

  const [randomStartTimes] = useState<number[]>(() =>
    items.map(() => Math.random() * 20),
  );

  useEffect(() => {
    const audio = new Audio();
    audio.src = '/assets/audios/tick.mp3';
    audio.volume = 0.5;
    audio.preload = 'auto';
    tickSoundRef.current = audio;

    return () => {
      if (tickSoundRef.current) {
        tickSoundRef.current.pause();
        tickSoundRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (selectedContributorSlug) {
      const index = items.findIndex(
        (item) => item.slug === selectedContributorSlug,
      );
      setScrollIndex(index);
      setAnimatedIndex(index);
      combinedAnimatedIndexRef.current = index;
      currentCombinedValueRef.current = index;
    }
  }, [selectedContributorSlug]);

  const playTickSound = useCallback(async () => {
    try {
      if (tickSoundRef.current) {
        tickSoundRef.current.currentTime = 0;
        await tickSoundRef.current.play().catch(() => {});
      }
    } catch (error) {
      console.warn(error);
    }
  }, []);

  useEffect(() => {
    const checkCenteredCard = (animatedValue: number) => {
      const lastValue = lastAnimatedValueRef.current;

      if (lastValue === null) {
        const roundedValue = Math.round(animatedValue);
        previousCenteredCardRef.current =
          ((roundedValue % items.length) + items.length) % items.length;
        lastIntegerCrossedRef.current = roundedValue;
        lastAnimatedValueRef.current = animatedValue;
        return;
      }

      const lastRounded = Math.round(lastValue);
      const currentRounded = Math.round(animatedValue);

      if (lastRounded !== currentRounded) {
        const direction = currentRounded > lastRounded ? 1 : -1;

        let integerToCheck = lastRounded + direction;
        while (integerToCheck !== currentRounded + direction) {
          const previousIndex = previousCenteredCardRef.current;
          const centeredIndex =
            ((integerToCheck % items.length) + items.length) % items.length;

          if (previousIndex !== null && previousIndex !== centeredIndex) {
            playTickSound();
            if (onCenteredItemChange) {
              onCenteredItemChange(centeredIndex);
            }
          }

          previousCenteredCardRef.current = centeredIndex;
          lastIntegerCrossedRef.current = integerToCheck;

          integerToCheck += direction;
        }
      }

      lastAnimatedValueRef.current = animatedValue;
    };

    const checkInterval = setInterval(() => {
      checkCenteredCard(combinedAnimatedIndexRef.current);
    }, 16); // ~60fps

    return () => {
      clearInterval(checkInterval);
    };
  }, [items.length, playTickSound, onCenteredItemChange]);

  useEffect(() => {
    if (!isDraggingRef.current && !isMomentumScrollingRef.current) {
      const targetValue = scrollIndex;
      const startValue = animatedIndexRef.current;
      const distance = targetValue - startValue;
      const duration = 800; //ms
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const easeOut = 1 - Math.pow(1 - progress, 3);

        animatedIndexRef.current = startValue + distance * easeOut;
        combinedAnimatedIndexRef.current = animatedIndexRef.current;
        currentCombinedValueRef.current = animatedIndexRef.current;

        setAnimatedIndex(animatedIndexRef.current);

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animate);
        }
      };

      animate();
    }
  }, [scrollIndex]);

  useEffect(() => {
    if (!shouldRunMomentum || !momentumAnimationRef.current) return;

    const animate = () => {
      if (!momentumAnimationRef.current) {
        setShouldRunMomentum(false);
        return;
      }

      const now = Date.now();
      const elapsed = (now - momentumAnimationRef.current.startTime) / 1000; // convert to seconds
      const deceleration = momentumAnimationRef.current.deceleration;

      const currentVelocity =
        momentumAnimationRef.current.velocity * Math.pow(deceleration, elapsed);

      if (Math.abs(currentVelocity) < 0.001) {
        momentumAnimationRef.current = null;
        isMomentumScrollingRef.current = false;
        setShouldRunMomentum(false);

        const snappedScrollIndex = Math.round(animatedIndexRef.current);
        setScrollIndex(snappedScrollIndex);
        return;
      }

      const deltaTime = 0.016;
      const deltaValue = currentVelocity * deltaTime;
      animatedIndexRef.current += deltaValue;
      combinedAnimatedIndexRef.current = animatedIndexRef.current;
      currentCombinedValueRef.current = animatedIndexRef.current;

      setAnimatedIndex(animatedIndexRef.current);

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [shouldRunMomentum]);

  const goToImage = useCallback(
    (targetIndex: number) => {
      setScrollIndex((currentScrollIndex) => {
        const totalItems = items.length;
        const currentCycle = Math.round(
          (currentScrollIndex - targetIndex) / totalItems,
        );
        return targetIndex + currentCycle * totalItems;
      });
    },
    [items.length],
  );

  const nextImage = useCallback(() => {
    setScrollIndex((prev) => prev - 1);
  }, []);

  const prevImage = useCallback(() => {
    setScrollIndex((prev) => prev + 1);
  }, []);

  const currentIndex =
    ((scrollIndex % items.length) + items.length) % items.length;

  const handleStart = useCallback((clientX: number) => {
    isDraggingRef.current = true;
    startXRef.current = clientX;
    lastXRef.current = clientX;
    lastTimeRef.current = Date.now();
    velocityRef.current = 0;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    momentumAnimationRef.current = null;

    startAnimatedValueRef.current = animatedIndexRef.current;
    dragOffsetRef.current = 0;
    combinedAnimatedIndexRef.current = animatedIndexRef.current;
    currentCombinedValueRef.current = animatedIndexRef.current;
  }, []);

  const handleMove = useCallback((clientX: number) => {
    if (!isDraggingRef.current) return;

    const currentTime = Date.now();
    const deltaX = clientX - startXRef.current;
    const timeDelta = currentTime - lastTimeRef.current;

    if (timeDelta > 0) {
      const dx = clientX - lastXRef.current;
      velocityRef.current = dx / timeDelta;
    }

    lastXRef.current = clientX;
    lastTimeRef.current = currentTime;

    const dragDistance = -deltaX; // Inverted to match visual movement
    const scrollOffset = dragDistance / DRAG_SENSITIVITY;
    dragOffsetRef.current = scrollOffset;

    const combinedValue = startAnimatedValueRef.current - scrollOffset;
    combinedAnimatedIndexRef.current = combinedValue;
    currentCombinedValueRef.current = combinedValue;

    setAnimatedIndex(combinedValue);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleEnd = useCallback((_clientX: number) => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;

    const currentCombinedValue = currentCombinedValueRef.current;
    const velocity = velocityRef.current / DRAG_SENSITIVITY;
    const absVelocity = Math.abs(velocity);

    animatedIndexRef.current = currentCombinedValue;
    dragOffsetRef.current = 0;
    combinedAnimatedIndexRef.current = currentCombinedValue;

    if (absVelocity > MIN_VELOCITY_FOR_MOMENTUM) {
      isMomentumScrollingRef.current = true;

      momentumAnimationRef.current = {
        velocity: velocity,
        deceleration: 0.998,
        startTime: Date.now(),
        startValue: currentCombinedValue,
      };
      setShouldRunMomentum(true);
    } else {
      const snappedScrollIndex = Math.round(currentCombinedValue);
      setScrollIndex(snappedScrollIndex);
    }
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      handleStart(e.clientX);
    },
    [handleStart],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      handleMove(e.clientX);
    },
    [handleMove],
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      handleEnd(e.clientX);
    },
    [handleEnd],
  );

  const handleMouseLeave = useCallback(() => {
    if (isDraggingRef.current) {
      handleEnd(lastXRef.current);
    }
  }, [handleEnd]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      handleStart(e.touches[0].clientX);
    },
    [handleStart],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.cancelable) {
        e.preventDefault();
      }
      handleMove(e.touches[0].clientX);
    },
    [handleMove],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      handleEnd(e.changedTouches[0].clientX);
    },
    [handleEnd],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if the carousel container is visible
      const carouselContainer = document.querySelector(
        '.fan-carousel-container',
      );
      if (
        !carouselContainer ||
        carouselContainer.classList.contains('hidden')
      ) {
        return; // Don't handle keyboard events if carousel is hidden
      }

      // Check if an input or textarea is focused
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement;

      // Don't handle carousel navigation if an input field is focused
      if (isInputFocused) {
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setScrollIndex((prev) => prev + 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setScrollIndex((prev) => prev - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <div className={`relative ${className}`}>
      {/* Large black glass semicircle - runs through center of fan circles, coming from bottom */}
      <div className="absolute bottom-0 left-1/2 transition-opacity transition-duration-400 -translate-x-1/2 w-[600px] h-[380px] bg-black/35 rounded-t-full z-10 pointer-events-none translate-y-3/4" />

      <div className="relative z-50">
        <div
          className="relative cursor-grab"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}>
          <div
            className="rounded-[4px] m-[2px] relative overflow-visible"
            style={{
              minHeight: '24px',
              minWidth: '340px',
            }}>
            <div
              className="relative flex items-center justify-center"
              style={{
                width: '100%',
                height: '180px',
                perspective: '800px',
                perspectiveOrigin: 'center center',
                transformStyle: 'preserve-3d',
              }}>
              {items.map((item, index) => (
                <VideoItem
                  key={index}
                  item={item}
                  index={index}
                  animatedIndex={animatedIndex}
                  totalItems={items.length}
                  randomStartTime={randomStartTimes[index]}
                  onPress={() => {
                    goToImage(index);
                    if (onItemClick) {
                      onItemClick(index);
                    }
                  }}
                />
              ))}
            </div>
          </div>

          {/* Navigation arrows */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-[-20px] sm:left-[-40px] top-1/2 transform -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-all z-50">
            <CarouselLeftArrowIcon />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-[-20px] sm:right-[-40px] top-1/2 transform -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-all z-50">
            <CarouselRightArrowIcon />
          </button>
        </div>

        {/* Album name display */}
        {items[currentIndex]?.artistName && (
          <div className="text-center -mt-10">
            <p className="text-white md:text-base lg:text-lg font-semibold transition-all duration-300">
              <span className="text-blue-100 block">
                @{items[currentIndex].artistAliasTag}
              </span>
              <span className="block text-sm">
                {items[currentIndex].artistName}
                {items[currentIndex].title && (
                  <span className="text-white/60">
                    {' '}
                    • {translateFanTitle(items[currentIndex].title, t)}
                  </span>
                )}
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
